/**
 * LPA Tags API
 *
 * CRUD operations for `core.mod_lpa_tags`.
 * Tags are tenant-scoped (no factory_id) so they can be shared across factories.
 */

import { supabase } from '@/integrations/supabase/client';
import type { LpaTag, CreateTagInput, UpdateTagInput } from '../types';

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

/** Fetch all tags for a tenant. Returns active + inactive. */
export async function fetchTags(tenantId: string): Promise<LpaTag[]> {
  const { data, error } = await supabase
    .schema('core')
    .from('mod_lpa_tags')
    .select('*')
    .eq('tenant_id', tenantId)
    .order('name');

  if (error) throw error;
  return (data ?? []) as unknown as LpaTag[];
}

/** Fetch a single tag by ID. */
export async function fetchTagById(tagId: string): Promise<LpaTag | null> {
  const { data, error } = await supabase
    .schema('core')
    .from('mod_lpa_tags')
    .select('*')
    .eq('id', tagId)
    .maybeSingle();

  if (error) throw error;
  return data as unknown as LpaTag | null;
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

/** Create a new tag. */
export async function createTag(input: CreateTagInput): Promise<LpaTag> {
  const { data, error } = await supabase
    .schema('core')
    .from('mod_lpa_tags')
    .insert({
      tenant_id: input.tenant_id,
      name: input.name,
      name_translations: input.name_translations ?? {},
      color: input.color ?? null,
    })
    .select()
    .single();

  if (error) throw error;
  return data as unknown as LpaTag;
}

/** Update an existing tag. Only provided fields are changed. */
export async function updateTag(
  tagId: string,
  input: UpdateTagInput,
): Promise<LpaTag> {
  const { data, error } = await supabase
    .schema('core')
    .from('mod_lpa_tags')
    .update(input)
    .eq('id', tagId)
    .select()
    .single();

  if (error) throw error;
  return data as unknown as LpaTag;
}

/** Delete a tag. Will cascade-delete question_tag_assignments. */
export async function deleteTag(tagId: string): Promise<void> {
  const { error } = await supabase
    .schema('core')
    .from('mod_lpa_tags')
    .delete()
    .eq('id', tagId);

  if (error) throw error;
}
